import { Modal } from 'components/atoms/Modal';
import styles from './index.module.scss';
import { IconCloseTablet } from 'components/atoms/icons/IconCloseTablet';
import { useTranslation } from 'react-i18next';
import { Field, Form, FormSpy } from 'react-final-form';
import { validateSelectField } from 'util/validator';
import arrayMutators from 'final-form-arrays';
import { SelectField } from 'components/molecules/SelectField';
import { Label } from 'components/atoms/Label';
import { TextAreaDataListField } from 'components/molecules/TextAreaDataListField';
import { ImageUploadButtons } from 'components/molecules/ImageUploadButtons';
import { useMemo, useState } from 'react';
import { getSlaYMDhm } from 'ts/formatDate';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { AbnormalityForm } from 'types/notice';
import { createNotificationAbnormality } from 'apis/notices';
import { toast } from 'react-toastify';
import { NoticeContent, CollectStatus } from 'util/Enums';
import { createOptionsFromEnum, mergeClasses } from 'util/commons';
import { ImagePreviewList } from './ImagePreviewList';
import heic2any from 'heic2any';

interface ReportAbnormalityModalProps {
  area: { name: string; id: string };
  targetIds?: string[];
  weight?: string;
  wasteUnitId?: number;
  onclose: () => void;
}

export const ReportAbnormalityModal = ({
  area,
  targetIds,
  weight,
  wasteUnitId,
  onclose,
}: ReportAbnormalityModalProps) => {
  const { t } = useTranslation();

  const user = useSelector(selectUser);

  const MAX_IMAGES = 5;

  const [previewImage, setPreviewImage] = useState<{
    url: string;
    needToConfirm?: boolean;
  } | null>(null);
  const [retakeAction, setRetakeAction] = useState<(() => void) | null>(null);
  const [reopenCameraAction, setReopenCameraAction] = useState<
    (() => void) | null
  >(null);
  const [isFormInvalid, setIsFormInvalid] = useState(true);

  const initValues = useMemo<AbnormalityForm>(
    () => ({
      areaId: area.id,
      type: '',
      collectState: '',
      targetIds,
      note: '',
      weight,
      wasteUnitId,
      images: [],
    }),
    [area.id, targetIds, weight, wasteUnitId]
  );

  const notificationContentOptions = createOptionsFromEnum(
    NoticeContent,
    'notification.modal.select_type_'
  );

  const notificationStatusOptions = createOptionsFromEnum(
    CollectStatus,
    'notification.modal.select_status_'
  );

  const handleFormSubmit = (formData: AbnormalityForm) => {
    let formattedForm: AbnormalityForm = {
      areaId: area.id,
      type: formData.type,
      collectState: formData.collectState,
      note: formData.note,
      images: formData.images,
    };

    if (targetIds?.length) {
      formattedForm = {
        ...formattedForm,
        targetIds: formData.targetIds,
      };
    } else {
      formattedForm = {
        ...formattedForm,
        weight: formData.weight,
        wasteUnitId: formData.wasteUnitId,
      };
    }
    createNotificationAbnormality(formattedForm, () => {
      toast.success(t('messages.M_010'));
      onclose();
    });
  };

  const handleImageSelect = async (
    files: File[],
    currentImages: Array<{ file: File; preview: string; base64: string }>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (value: any) => void,
    needToConfirm = false,
    onRetake?: () => void
  ) => {
    const remainingSlots = MAX_IMAGES - currentImages.length;
    const filesToAdd = files.slice(0, remainingSlots);

    const newImagePromises = filesToAdd.map(async (file) => {
      let processedFile = file;

      const isHeic =
        file.type === 'image/heic' ||
        file.type === 'image/heif' ||
        file.name.toLowerCase().endsWith('.heic') ||
        file.name.toLowerCase().endsWith('.heif');

      if (isHeic) {
        try {
          const convertedBlob = await heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: 0.9,
          });

          const blob = Array.isArray(convertedBlob)
            ? convertedBlob[0]
            : convertedBlob;

          const fileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
          processedFile = new File([blob], fileName, { type: 'image/jpeg' });
        } catch (error) {
          console.error(error);
        }
      }

      return new Promise<{ file: File; preview: string; base64: string }>(
        (resolve) => {
          const preview = URL.createObjectURL(processedFile);
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64 = reader.result as string;
            resolve({ file: processedFile, preview, base64 });
          };
          reader.readAsDataURL(processedFile);
        }
      );
    });

    const newImages = await Promise.all(newImagePromises);
    onChange([...currentImages, ...newImages]);

    if (needToConfirm && newImages.length) {
      const previewTarget = newImages[0];
      setPreviewImage({ url: previewTarget.preview, needToConfirm });
      setReopenCameraAction(() => onRetake || null);

      setRetakeAction(() => () => {
        URL.revokeObjectURL(previewTarget.preview);
        onChange([
          ...currentImages,
          ...newImages.filter(
            (image) => image.preview !== previewTarget.preview
          ),
        ]);
      });
    }
  };

  const handleRemoveImage = (
    index: number,
    currentImages: Array<{ file: File; preview: string; base64: string }>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (value: any) => void
  ) => {
    URL.revokeObjectURL(currentImages[index].preview);
    const newImages = currentImages.filter((_, i) => i !== index);
    onChange(newImages);
  };

  const handleImageClick = (preview: string) => {
    setPreviewImage({ url: preview });
  };

  const handleClosePreview = () => {
    setReopenCameraAction(null);
    setRetakeAction(null);
    setPreviewImage(null);
  };

  const handleUsePhoto = () => {
    setReopenCameraAction(null);
    setRetakeAction(null);
    setPreviewImage(null);
  };

  const handleRetakePhoto = () => {
    if (retakeAction) {
      retakeAction();
    }
    if (reopenCameraAction) {
      reopenCameraAction();
    }
    setReopenCameraAction(null);
    setRetakeAction(null);
    setPreviewImage(null);
  };

  return (
    <div className={styles.customModal}>
      <Modal>
        <div className={styles.reportAbnormalityModal}>
          <div className={styles.header}>
            <div className={styles.title}>
              {t('confirm_select_route_item.report_abnormal_modal.title')}
              <br />
            </div>

            <IconCloseTablet onClick={onclose} />
          </div>

          <div className={styles.body}>
            <Form<AbnormalityForm>
              initialValues={initValues}
              onSubmit={handleFormSubmit}
              validate={(values) => {
                return {
                  type: validateSelectField(
                    t(
                      'confirm_select_route_item.report_abnormal_modal.form.notification_content'
                    ),
                    values.type
                  ),
                  collectState: validateSelectField(
                    t(
                      'confirm_select_route_item.report_abnormal_modal.form.collection_status'
                    ),
                    values.collectState
                  ),
                };
              }}
              mutators={{
                changeValue: ([field, value], state, { changeValue }) => {
                  changeValue(state, field, () => value);
                },
                ...arrayMutators,
              }}
            >
              {(props) => (
                <>
                  <FormSpy
                    subscription={{ invalid: true }}
                    onChange={(state) => setIsFormInvalid(state.invalid)}
                  />
                  <form
                    className={styles.formInput}
                    id="reportAbnormalForm"
                    onSubmit={props.handleSubmit}
                  >
                    <div className={styles.inputFrame}>
                      <div className={styles.info}>
                        <div className={styles.label}>
                          {t(
                            'confirm_select_route_item.report_abnormal_modal.form.current_time'
                          )}
                        </div>
                        <span className={styles.split}>:</span>
                        <div className={styles.value}>
                          {getSlaYMDhm(new Date().toISOString())}
                        </div>
                        <div className={styles.label}>
                          {t(
                            'confirm_select_route_item.report_abnormal_modal.form.place'
                          )}
                        </div>
                        <span className={styles.split}>:</span>
                        <div className={styles.value}>{area.name}</div>
                        <div className={styles.label}>
                          {t(
                            'confirm_select_route_item.report_abnormal_modal.form.person_in_charge'
                          )}
                        </div>
                        <span className={styles.split}>:</span>
                        <div className={styles.value}>{user.name}</div>
                      </div>

                      <SelectField
                        label={t(
                          'confirm_select_route_item.report_abnormal_modal.form.notification_content'
                        )}
                        name="type"
                        placeholder={t('common.select') || '選択する'}
                        options={notificationContentOptions}
                        disabled={!notificationContentOptions.length}
                        isDarkmode
                        required
                      />

                      <SelectField
                        label={t(
                          'confirm_select_route_item.report_abnormal_modal.form.collection_status'
                        )}
                        name="collectState"
                        placeholder={t('common.select') || '選択する'}
                        options={notificationStatusOptions}
                        disabled={!notificationStatusOptions.length}
                        isDarkmode
                        required
                      />

                      <TextAreaDataListField
                        name={'note'}
                        autoFocus={false}
                        label={
                          t(
                            'confirm_select_route_item.report_abnormal_modal.form.note'
                          ) || ''
                        }
                      />

                      <Field name="images">
                        {({ input }) => {
                          const currentImages = input.value || [];
                          return (
                            <div className={styles.uploadSection}>
                              <Label
                                className={styles.label}
                                text={
                                  t(
                                    'confirm_select_route_item.report_abnormal_modal.form.add_photo'
                                  ) || '写真'
                                }
                              />
                              <ImageUploadButtons
                                onImageSelect={(
                                  files,
                                  needToConfirm,
                                  onRetake
                                ) =>
                                  handleImageSelect(
                                    files,
                                    currentImages,
                                    input.onChange,
                                    needToConfirm,
                                    onRetake
                                  )
                                }
                                disabled={currentImages.length >= MAX_IMAGES}
                                maxFilesLength={MAX_IMAGES}
                                currentImagesLength={currentImages.length}
                                imagePreviewList={
                                  <ImagePreviewList
                                    images={currentImages}
                                    onImageClick={handleImageClick}
                                    onRemoveImage={(index) =>
                                      handleRemoveImage(
                                        index,
                                        currentImages,
                                        input.onChange
                                      )
                                    }
                                  />
                                }
                              />

                              <ImagePreviewList
                                images={currentImages}
                                onImageClick={handleImageClick}
                                onRemoveImage={(index) =>
                                  handleRemoveImage(
                                    index,
                                    currentImages,
                                    input.onChange
                                  )
                                }
                              />
                            </div>
                          );
                        }}
                      </Field>
                    </div>
                  </form>
                </>
              )}
            </Form>
          </div>
          <div className={styles.footer}>
            <button
              className={styles.submitBtn}
              type="submit"
              form="reportAbnormalForm"
              disabled={isFormInvalid}
            >
              {t('confirm_select_route_item.report_abnormal_modal.submit_btn')}
            </button>
          </div>
        </div>

        {previewImage && previewImage.url && (
          <Modal>
            <div
              className={mergeClasses(styles.imagePreviewModal, {
                [styles.confirm]: previewImage.needToConfirm,
              })}
            >
              {!previewImage.needToConfirm && (
                <button
                  className={styles.closePreviewButton}
                  onClick={handleClosePreview}
                >
                  <IconCloseTablet />
                </button>
              )}

              <div className={styles.previewImageWrapper}>
                <img
                  src={previewImage.url}
                  alt="Preview"
                  className={styles.largePreviewImage}
                />
              </div>

              {previewImage.needToConfirm && (
                <div className={styles.confirmButtons}>
                  <button onClick={handleUsePhoto}>
                    {t('common.button.use_photo')}
                  </button>
                  <button
                    onClick={handleRetakePhoto}
                    className={styles.retakeBtn}
                  >
                    {t('common.button.retake_photo')}
                  </button>
                </div>
              )}
            </div>
          </Modal>
        )}
      </Modal>
    </div>
  );
};
